import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { MessageCircle, X, Send, Loader2, Mail } from 'lucide-react';
import { createClient, type RealtimeChannel } from '@supabase/supabase-js';
import { LinkifyMessage } from '../utils/linkifyMessage';
import {
  contactChatFetchWithRetry,
  friendlyNetworkError,
  parseContactChatErrorResponse,
} from '../utils/contactChatClient';

declare global {
  interface Window {
    grecaptcha?: {
      ready: (callback: () => void) => void;
      execute: (siteKey: string, options: { action: string }) => Promise<string>;
    };
  }
}

const LS_VISITOR = 'contact_chat_visitor_token';
const LS_CONV = 'contact_chat_conversation_id';
const LS_GUEST_JWT = 'contact_chat_guest_jwt';
const RECAPTCHA_SITE_KEY = '6Lfj-YsrAAAAAAIPxGyd7OeWWZT3YWcXtegCPH_j';

type ChatRow = {
  id: string;
  sender_role: 'customer' | 'admin';
  message: string;
  created_at: string;
  attachments?: unknown;
};

function imagePathsFromAttachments(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((a) => {
      if (a && typeof a === 'object' && 'path' in a && typeof (a as { path: unknown }).path === 'string') {
        return (a as { path: string }).path;
      }
      if (
        a &&
        typeof a === 'object' &&
        'storage_path' in a &&
        typeof (a as { storage_path: unknown }).storage_path === 'string'
      ) {
        return (a as { storage_path: string }).storage_path;
      }
      return null;
    })
    .filter((p): p is string => Boolean(p));
}

function publicChatImageUrl(storagePath: string): string {
  const base = (import.meta.env.VITE_SUPABASE_URL ?? '').replace(/\/$/, '');
  return `${base}/storage/v1/object/public/chat/${storagePath.replace(/^\/+/, '')}`;
}

const ContactChatWidget: React.FC = () => {
  const location = useLocation();
  const hideOnAdmin = location.pathname.startsWith('/admin');
  const hideOnPortal = location.pathname.startsWith('/mina-bokningar');

  const [open, setOpen] = useState(false);
  const [recaptchaReady, setRecaptchaReady] = useState(false);
  const [visitorToken, setVisitorToken] = useState<string | null>(() => localStorage.getItem(LS_VISITOR));
  const [conversationId, setConversationId] = useState<string | null>(() => localStorage.getItem(LS_CONV));

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [subject, setSubject] = useState('');
  const [privacyOk, setPrivacyOk] = useState(false);
  const [termsOk, setTermsOk] = useState(false);
  const [firstText, setFirstText] = useState('');
  const [sendingFirst, setSendingFirst] = useState(false);

  const [messages, setMessages] = useState<ChatRow[]>([]);
  const [draft, setDraft] = useState('');
  const [loadingThread, setLoadingThread] = useState(false);
  const [sendingFollow, setSendingFollow] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** Mjuk status vid upprepade tysta misslyckanden (polling) – inte samma som error. */
  const [syncBanner, setSyncBanner] = useState<string | null>(null);
  /** Efter första skickade meddelande: tips om e-post + att chatten kan stängas. */
  const [postFirstMessageInfoVisible, setPostFirstMessageInfoVisible] = useState(false);

  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const realtimeChannelRef = useRef<RealtimeChannel | null>(null);
  const realtimeClientRef = useRef<ReturnType<typeof createClient> | null>(null);
  const listEndRef = useRef<HTMLDivElement | null>(null);
  const isFirstThreadLoadRef = useRef(true);
  const silentPollFailuresRef = useRef(0);
  /** Sätt bara vid misslyckad trådläsning (inte vid skicka) – rensar error när poll lyckas. */
  const loadThreadErrorPendingRef = useRef(false);
  const sendingFirstRef = useRef(false);
  const sendingFollowRef = useRef(false);

  const policyVersion = import.meta.env.VITE_PRIVACY_POLICY_VERSION ?? '2025-04';
  const termsVersion = import.meta.env.VITE_TERMS_VERSION ?? '2025-04';

  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL ?? '';
  const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY ?? '';

  const edgeUrl = `${supabaseUrl.replace(/\/$/, '')}/functions/v1/contact-chat`;

  /** Samma som @supabase/supabase-js: både Authorization och apikey krävs av API-gatewayen i vissa lägen. */
  const chatFetchHeaders = useCallback(
    (jsonBody: boolean): HeadersInit => {
      const h: Record<string, string> = {
        Authorization: `Bearer ${anonKey}`,
        apikey: anonKey,
      };
      if (jsonBody) h['Content-Type'] = 'application/json';
      return h;
    },
    [anonKey]
  );

  const clearChatSession = useCallback(() => {
    localStorage.removeItem(LS_VISITOR);
    localStorage.removeItem(LS_CONV);
    localStorage.removeItem(LS_GUEST_JWT);
    if (realtimeChannelRef.current && realtimeClientRef.current) {
      void realtimeClientRef.current.removeChannel(realtimeChannelRef.current);
      realtimeChannelRef.current = null;
    }
    realtimeClientRef.current = null;
    setVisitorToken(null);
    setConversationId(null);
    setMessages([]);
    isFirstThreadLoadRef.current = true;
    silentPollFailuresRef.current = 0;
    loadThreadErrorPendingRef.current = false;
    setSyncBanner(null);
    setPostFirstMessageInfoVisible(false);
  }, []);

  const bumpSilentPollFailure = useCallback(() => {
    silentPollFailuresRef.current += 1;
    if (silentPollFailuresRef.current >= 4) {
      setSyncBanner(
        'Uppdateringen är tillfälligt långsam. Dina skickade meddelanden finns kvar — du kan fortsätta skriva.'
      );
    }
  }, []);

  const clearSilentPollFailure = useCallback(() => {
    silentPollFailuresRef.current = 0;
    setSyncBanner(null);
  }, []);

  useEffect(() => {
    const tick = () => {
      if (window.grecaptcha?.ready) {
        window.grecaptcha.ready(() => setRecaptchaReady(true));
      }
    };
    tick();
    const t = window.setInterval(tick, 200);
    return () => window.clearInterval(t);
  }, []);

  const mintGuestSession = useCallback(
    async (vt: string, convId: string): Promise<string | null> => {
      try {
        const res = await contactChatFetchWithRetry(edgeUrl, {
          method: 'POST',
          headers: chatFetchHeaders(true),
          body: JSON.stringify({
            action: 'mint_session',
            visitorToken: vt,
            conversationId: convId,
          }),
        });
        if (!res.ok) return null;
        const json = (await res.json()) as { access_token?: string };
        if (json.access_token) {
          localStorage.setItem(LS_GUEST_JWT, json.access_token);
          return json.access_token;
        }
      } catch {
        /* polling fallback */
      }
      return null;
    },
    [edgeUrl, chatFetchHeaders]
  );

  const mergeChatRow = useCallback((row: ChatRow) => {
    setMessages((prev) => {
      if (prev.some((m) => m.id === row.id)) return prev;
      return [...prev, row].sort(
        (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
      );
    });
    clearSilentPollFailure();
  }, [clearSilentPollFailure]);

  const runCaptcha = useCallback(async (action: string) => {
    if (!window.grecaptcha?.execute) {
      throw new Error('Säkerhetskontrollen laddas fortfarande. Vänta en stund eller uppdatera sidan.');
    }
    try {
      return await window.grecaptcha.execute(RECAPTCHA_SITE_KEY, { action });
    } catch {
      throw new Error('Säkerhetskontrollen misslyckades. Uppdatera sidan och försök igen.');
    }
  }, []);

  const loadThread = useCallback(
    async (opts?: { silent?: boolean }) => {
      const silent = opts?.silent ?? false;
      const vt = localStorage.getItem(LS_VISITOR);
      if (!vt) return;

      if (!silent && isFirstThreadLoadRef.current) {
        setLoadingThread(true);
      }

      try {
        const res = await contactChatFetchWithRetry(
          `${edgeUrl}?visitor_token=${encodeURIComponent(vt)}`,
          { headers: chatFetchHeaders(false) }
        );

        if (!res.ok) {
          if (silent) {
            bumpSilentPollFailure();
            return;
          }
          const p = await parseContactChatErrorResponse(res);
          if (p.resetSession) clearChatSession();
          else {
            setError(p.message);
            loadThreadErrorPendingRef.current = true;
          }
          return;
        }

        const json = (await res.json()) as { messages?: ChatRow[] };
        setMessages(json.messages ?? []);
        clearSilentPollFailure();
        isFirstThreadLoadRef.current = false;
        if (loadThreadErrorPendingRef.current) {
          loadThreadErrorPendingRef.current = false;
          setError(null);
        }
      } catch (e) {
        if (silent) {
          bumpSilentPollFailure();
          return;
        }
        setError(friendlyNetworkError(e));
        loadThreadErrorPendingRef.current = true;
      } finally {
        if (!silent) setLoadingThread(false);
      }
    },
    [
      edgeUrl,
      chatFetchHeaders,
      bumpSilentPollFailure,
      clearSilentPollFailure,
      clearChatSession,
    ]
  );

  // Realtime (v2) + polling som fallback – se docs/CONTACT_CHAT_V2.md
  useEffect(() => {
    if (!open || !visitorToken || !conversationId) return;

    let cancelled = false;

    const setupRealtime = async () => {
      let token = localStorage.getItem(LS_GUEST_JWT);
      if (!token) {
        token = await mintGuestSession(visitorToken, conversationId);
      }
      if (!token || cancelled) return;

      if (realtimeChannelRef.current && realtimeClientRef.current) {
        await realtimeClientRef.current.removeChannel(realtimeChannelRef.current);
        realtimeChannelRef.current = null;
      }

      const guestClient = createClient(supabaseUrl, anonKey, {
        auth: { persistSession: false, autoRefreshToken: false, detectSessionInUrl: false },
      });
      guestClient.realtime.setAuth(token);
      realtimeClientRef.current = guestClient;

      const channel = guestClient
        .channel(`contact-chat-${conversationId}`)
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'contact_messages',
            filter: `conversation_id=eq.${conversationId}`,
          },
          (payload) => {
            const row = payload.new as ChatRow;
            if (row?.id) mergeChatRow(row);
          }
        )
        .subscribe();

      realtimeChannelRef.current = channel;
    };

    void setupRealtime();

    return () => {
      cancelled = true;
      if (realtimeChannelRef.current && realtimeClientRef.current) {
        void realtimeClientRef.current.removeChannel(realtimeChannelRef.current);
        realtimeChannelRef.current = null;
      }
    };
  }, [open, visitorToken, conversationId, supabaseUrl, anonKey, mintGuestSession, mergeChatRow]);

  useEffect(() => {
    if (!open || !visitorToken) return;
    void loadThread({ silent: false });
    const pollMs = () => (document.visibilityState === 'visible' ? 30000 : 60000);
    const restart = () => {
      if (pollRef.current) window.clearInterval(pollRef.current);
      pollRef.current = window.setInterval(() => void loadThread({ silent: true }), pollMs());
    };
    restart();
    const onVis = () => restart();
    document.addEventListener('visibilitychange', onVis);
    return () => {
      document.removeEventListener('visibilitychange', onVis);
      if (pollRef.current) window.clearInterval(pollRef.current);
    };
  }, [open, visitorToken, loadThread]);

  useEffect(() => {
    const onOnline = () => {
      if (open && visitorToken) {
        silentPollFailuresRef.current = 0;
        setSyncBanner(null);
        void loadThread({ silent: true });
      }
    };
    window.addEventListener('online', onOnline);
    return () => window.removeEventListener('online', onOnline);
  }, [open, visitorToken, loadThread]);

  useEffect(() => {
    listEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, open]);

  const submitFirst = async (e: React.FormEvent) => {
    e.preventDefault();
    if (sendingFirstRef.current) return;
    setError(null);
    if (!privacyOk || !termsOk) {
      setError('Du måste godkänna integritetspolicy och villkor.');
      return;
    }
    if (!firstText.trim() || !name.trim() || !email.trim()) {
      setError('Fyll i namn, e-post och meddelande.');
      return;
    }
    if (!recaptchaReady) {
      setError('reCAPTCHA laddas – försök igen om ett ögonblick.');
      return;
    }
    sendingFirstRef.current = true;
    setSendingFirst(true);
    try {
      const captchaToken = await runCaptcha('contact_chat_first');
      const res = await contactChatFetchWithRetry(edgeUrl, {
        method: 'POST',
        headers: chatFetchHeaders(true),
        body: JSON.stringify({
          action: 'first_message',
          captchaToken,
          privacyAccepted: true,
          termsAccepted: true,
          name: name.trim(),
          email: email.trim(),
          phone: phone.trim() || undefined,
          subject: subject.trim() || undefined,
          message: firstText.trim(),
          policyVersion,
          termsVersion,
          attachments: [],
        }),
      });
      if (!res.ok) {
        const p = await parseContactChatErrorResponse(res);
        if (p.resetSession) clearChatSession();
        throw new Error(p.message);
      }
      const json = (await res.json()) as {
        visitor_token?: string;
        conversation_id?: string;
        access_token?: string;
      };
      if (json.visitor_token && json.conversation_id) {
        localStorage.setItem(LS_VISITOR, json.visitor_token);
        localStorage.setItem(LS_CONV, json.conversation_id);
        if (json.access_token) {
          localStorage.setItem(LS_GUEST_JWT, json.access_token);
        }
        setVisitorToken(json.visitor_token);
        setConversationId(json.conversation_id);
        isFirstThreadLoadRef.current = true;
      }
      setFirstText('');
      setPostFirstMessageInfoVisible(true);
      await loadThread({ silent: false });
    } catch (err) {
      setError(friendlyNetworkError(err));
    } finally {
      sendingFirstRef.current = false;
      setSendingFirst(false);
    }
  };

  const uploadAndSendImage = async (file: File) => {
    if (!visitorToken || !conversationId) return;
    setUploading(true);
    setError(null);
    try {
      const captchaToken = await runCaptcha('contact_chat_upload');
      const dataUrl = await new Promise<string>((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(r.result as string);
        r.onerror = () => reject(new Error('Kunde inte läsa filen'));
        r.readAsDataURL(file);
      });
      const comma = dataUrl.indexOf(',');
      const base64 = comma >= 0 ? dataUrl.slice(comma + 1) : dataUrl;

      const up = await contactChatFetchWithRetry(edgeUrl, {
        method: 'POST',
        headers: chatFetchHeaders(true),
        body: JSON.stringify({
          action: 'upload_image',
          captchaToken,
          visitorToken,
          conversationId,
          fileBase64: base64,
          mimeType: file.type,
        }),
      });
      if (!up.ok) {
        const p = await parseContactChatErrorResponse(up);
        if (p.resetSession) clearChatSession();
        throw new Error(p.message);
      }
      const upJson = (await up.json()) as { storage_path?: string };

      const path = upJson.storage_path;
      if (!path) throw new Error('Bilden kunde inte sparas. Försök med en mindre bild.');

      const captcha2 = await runCaptcha('contact_chat_send');
      const fu = await contactChatFetchWithRetry(edgeUrl, {
        method: 'POST',
        headers: chatFetchHeaders(true),
        body: JSON.stringify({
          action: 'followup',
          captchaToken: captcha2,
          visitorToken,
          conversationId,
          message: draft.trim() || '(Bifogad bild)',
          attachments: [{ kind: 'image', path }],
        }),
      });
      if (!fu.ok) {
        const p = await parseContactChatErrorResponse(fu);
        if (p.resetSession) clearChatSession();
        throw new Error(p.message);
      }
      setDraft('');
      await loadThread({ silent: true });
    } catch (err) {
      setError(friendlyNetworkError(err));
    } finally {
      setUploading(false);
    }
  };

  const sendFollowup = async (e: React.FormEvent) => {
    e.preventDefault();
    if (sendingFollowRef.current) return;
    if (!visitorToken || !conversationId) return;
    if (!draft.trim()) return;
    sendingFollowRef.current = true;
    setSendingFollow(true);
    setError(null);
    try {
      const captchaToken = await runCaptcha('contact_chat_send');
      const res = await contactChatFetchWithRetry(edgeUrl, {
        method: 'POST',
        headers: chatFetchHeaders(true),
        body: JSON.stringify({
          action: 'followup',
          captchaToken,
          visitorToken,
          conversationId,
          message: draft.trim(),
          attachments: [],
        }),
      });
      if (!res.ok) {
        const p = await parseContactChatErrorResponse(res);
        if (p.resetSession) clearChatSession();
        throw new Error(p.message);
      }
      setDraft('');
      await loadThread({ silent: true });
    } catch (err) {
      setError(friendlyNetworkError(err));
    } finally {
      sendingFollowRef.current = false;
      setSendingFollow(false);
    }
  };

  if (hideOnAdmin || hideOnPortal) {
    return null;
  }

  return (
    <div className="fixed bottom-4 right-4 z-[100] flex flex-col items-end gap-2 pointer-events-none">
      <div className="pointer-events-auto flex items-center gap-2 mr-1">
        <span className="text-sm text-slate-300 drop-shadow-md max-w-[min(200px,40vw)] text-right leading-tight">
          Kan vi hjälpa dig?
        </span>
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-violet-600 to-violet-900 text-white shadow-lg shadow-violet-900/50 ring-2 ring-white/20 hover:scale-105 hover:from-violet-500 hover:to-violet-800 transition-transform focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400/80"
          aria-label="Öppna chatt"
        >
          <MessageCircle className="h-7 w-7" strokeWidth={2.25} aria-hidden />
        </button>
      </div>

      {open && (
        <div className="pointer-events-auto w-[min(100vw-2rem,380px)] max-h-[min(85vh,560px)] flex flex-col rounded-2xl border border-slate-700/80 bg-slate-900/95 shadow-2xl backdrop-blur-md overflow-hidden">
          {(!supabaseUrl || !anonKey) && (
            <p className="px-4 py-2 text-xs text-amber-300 bg-amber-950/50 border-b border-amber-800/30">
              Chatt saknar konfiguration (VITE_SUPABASE_URL / VITE_SUPABASE_ANON_KEY). Bygg om sajten med rätt miljövariabler.
            </p>
          )}
          <div className="flex items-center justify-between gap-2 px-4 py-3 border-b border-slate-700/80 bg-slate-800/80">
            <div className="flex items-center gap-2 min-w-0">
              <MessageCircle className="text-cyan-400 flex-shrink-0" size={20} />
              <span className="font-semibold text-white truncate">Chatt med oss</span>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="p-1 rounded-lg text-slate-400 hover:text-white hover:bg-slate-700"
              aria-label="Stäng chatt"
            >
              <X size={20} />
            </button>
          </div>

          {!visitorToken ? (
            <form onSubmit={submitFirst} className="flex flex-col flex-1 min-h-0 overflow-y-auto p-4 space-y-3 text-sm">
              <p className="text-slate-400 text-xs">Fyll i dina uppgifter så vi kan återkomma.</p>
              <input
                className="w-full rounded-lg border border-slate-600 bg-slate-800 px-3 py-2 text-white placeholder-slate-500"
                placeholder="Namn *"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
              <input
                type="email"
                className="w-full rounded-lg border border-slate-600 bg-slate-800 px-3 py-2 text-white placeholder-slate-500"
                placeholder="E-post *"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
              <input
                className="w-full rounded-lg border border-slate-600 bg-slate-800 px-3 py-2 text-white placeholder-slate-500"
                placeholder="Telefon"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
              />
              <input
                className="w-full rounded-lg border border-slate-600 bg-slate-800 px-3 py-2 text-white placeholder-slate-500"
                placeholder="Ämne"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
              />
              <textarea
                className="w-full min-h-[100px] rounded-lg border border-slate-600 bg-slate-800 px-3 py-2 text-white placeholder-slate-500"
                placeholder="Ditt meddelande *"
                value={firstText}
                onChange={(e) => setFirstText(e.target.value)}
                required
              />
              <label className="flex items-start gap-2 text-xs text-slate-300 cursor-pointer">
                <input
                  type="checkbox"
                  checked={privacyOk}
                  onChange={(e) => setPrivacyOk(e.target.checked)}
                  className="mt-0.5 rounded border-slate-600"
                />
                <span>
                  Jag har läst och godkänner{' '}
                  <Link to="/integritet" className="text-cyan-400 underline" target="_blank" rel="noopener noreferrer">
                    integritetspolicyn
                  </Link>
                  .
                </span>
              </label>
              <label className="flex items-start gap-2 text-xs text-slate-300 cursor-pointer">
                <input
                  type="checkbox"
                  checked={termsOk}
                  onChange={(e) => setTermsOk(e.target.checked)}
                  className="mt-0.5 rounded border-slate-600"
                />
                <span>
                  Jag godkänner{' '}
                  <Link to="/villkor" className="text-cyan-400 underline" target="_blank" rel="noopener noreferrer">
                    användarvillkoren
                  </Link>
                  .
                </span>
              </label>
              {error && <p className="text-red-400 text-xs">{error}</p>}
              <button
                type="submit"
                disabled={sendingFirst || !recaptchaReady}
                className="flex items-center justify-center gap-2 rounded-lg bg-violet-600 hover:bg-violet-500 disabled:opacity-50 text-white font-medium py-2.5"
              >
                {sendingFirst ? <Loader2 className="animate-spin" size={18} /> : <Send size={18} />}
                Skicka
              </button>
            </form>
          ) : (
            <div className="flex flex-col flex-1 min-h-0">
              {postFirstMessageInfoVisible && (
                <div
                  className="flex gap-2 items-start px-3 py-2.5 text-xs text-slate-200 bg-cyan-950/45 border-b border-cyan-800/35"
                  role="status"
                >
                  <Mail className="h-4 w-4 shrink-0 text-cyan-400 mt-0.5" aria-hidden />
                  <p className="flex-1 leading-snug pr-1">
                    <span className="block">Tack för ditt meddelande! Vi återkommer inom kort.</span>
                    <span className="block mt-1.5 text-slate-300/95">
                      Du kan stänga chatten när som helst – svaret skickas både hit och till din e-post.
                    </span>
                  </p>
                  <button
                    type="button"
                    onClick={() => setPostFirstMessageInfoVisible(false)}
                    className="shrink-0 p-1 rounded-md text-slate-400 hover:text-white hover:bg-slate-700/80"
                    aria-label="Dölj information"
                  >
                    <X size={16} />
                  </button>
                </div>
              )}
              {syncBanner && (
                <p className="px-3 py-2 text-xs text-amber-100/90 bg-amber-950/35 border-b border-amber-800/25 leading-snug">
                  {syncBanner}
                </p>
              )}
              <div className="flex-1 overflow-y-auto p-3 space-y-3 min-h-[200px]">
                {loadingThread && messages.length === 0 && (
                  <div className="flex justify-center py-8 text-slate-500">
                    <Loader2 className="animate-spin" />
                  </div>
                )}
                {messages.map((m) => (
                  <div
                    key={m.id}
                    className={`rounded-xl px-3 py-2 text-sm ${
                      m.sender_role === 'admin'
                        ? 'bg-emerald-900/40 border border-emerald-700/40 ml-0 mr-6'
                        : 'bg-slate-800 border border-slate-700 ml-6 mr-0'
                    }`}
                  >
                    <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-1">
                      {m.sender_role === 'admin' ? 'Festutrustning' : 'Du'}
                    </div>
                    <div className="text-slate-100">
                      <LinkifyMessage text={m.message} />
                    </div>
                    {imagePathsFromAttachments(m.attachments).map((path) => (
                      <a
                        key={path}
                        href={publicChatImageUrl(path)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-2 block"
                      >
                        <img
                          src={publicChatImageUrl(path)}
                          alt=""
                          loading="lazy"
                          className="max-w-full max-h-48 rounded-lg border border-slate-600 object-contain"
                        />
                      </a>
                    ))}
                  </div>
                ))}
                <div ref={listEndRef} />
              </div>

              <form onSubmit={sendFollowup} className="border-t border-slate-700/80 p-3 space-y-2">
                {error && <p className="text-red-400 text-xs">{error}</p>}
                <textarea
                  className="w-full min-h-[72px] rounded-lg border border-slate-600 bg-slate-800 px-3 py-2 text-sm text-white placeholder-slate-500"
                  placeholder="Skriv ett meddelande…"
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                />
                <div className="flex items-center gap-2">
                  <label className="text-xs text-slate-400 cursor-pointer flex items-center gap-1">
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp,image/avif,image/gif"
                      className="sr-only"
                      disabled={uploading || sendingFollow}
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        e.target.value = '';
                        if (f) void uploadAndSendImage(f);
                      }}
                    />
                    <span className="px-2 py-1 rounded bg-slate-700 hover:bg-slate-600 text-slate-200">
                      {uploading ? 'Laddar…' : 'Bild'}
                    </span>
                  </label>
                  <button
                    type="submit"
                    disabled={sendingFollow || !draft.trim() || uploading}
                    className="flex-1 flex items-center justify-center gap-2 rounded-lg bg-violet-600 hover:bg-violet-500 disabled:opacity-50 text-white text-sm font-medium py-2"
                  >
                    {sendingFollow ? <Loader2 className="animate-spin" size={16} /> : <Send size={16} />}
                    Skicka
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ContactChatWidget;
